/**
 * ProShip Direct — subscription plans.
 *
 * The plans themselves come from GET direct/plans (see toPlanCard in api.js). What is left here is
 * the custom-plan card, the comparison copy and the GST fallback.
 *
 * What a plan does NOT include is as important as what it does: buyer communications (WhatsApp,
 * SMS, email) are metered separately and activated from Settings. A merchant who assumes their
 * plan covers notifications gets a surprise on the invoice, so every surface that shows a price
 * says so.
 */

export const GST_RATE = 0.18;

export const CUSTOM_PLAN = {
  title: 'More than 20,000 shipments a month?',
  body: 'Volume-based pricing, dedicated support and custom integrations.',
  contact: 'sales@prozo.com',
};

/**
 * GST and total.
 *
 * PREFER THE SERVER'S NUMBERS. A plan card from GET direct/plans carries gstAmount and
 * totalPayable computed from that plan's own gstRate — which is a negotiable term, not a
 * constant (an SEZ or export merchant is not on 18%). Recomputing locally against GST_RATE
 * would show one figure in the modal and debit another from the wallet.
 *
 * The local maths stays as a fallback for the one card the backend does not own: CUSTOM_PLAN,
 * and any fixture still rendered before the catalog has loaded.
 */
export const planGst = (plan) =>
  plan?.gstAmount !== null && plan?.gstAmount !== undefined
    ? Number(plan.gstAmount)
    : Math.round((plan?.price || 0) * GST_RATE);

export const planTotal = (plan) =>
  plan?.totalPayable !== null && plan?.totalPayable !== undefined
    ? Number(plan.totalPayable)
    : (plan?.price || 0) + planGst(plan);

/**
 * How ProShip Direct differs from shipping on ProShip's own couriers.
 *
 * Carried over from the comparison in Ashvini's Operating Mode screen — the facts were the
 * genuinely useful part of that page, and they belong here, where a merchant is deciding whether
 * to buy rather than which mode to run.
 */
export const DIRECT_VS_PROSHIP = [
  {
    fact: 'Courier accounts',
    direct: 'Contracts you hold yourself',
    proship: 'ProShip courier codes, ready to use',
  },
  {
    fact: 'Freight invoiced by',
    direct: 'Your carriers, direct to you',
    proship: 'ProShip, on one invoice',
  },
  {
    fact: 'Rate negotiation',
    direct: 'Whatever you negotiated',
    proship: 'ProShip’s negotiated rates',
  },
  {
    fact: 'Who sees your rate card',
    direct: 'Encrypted, and used only to price and rank your own shipments',
    proship: 'ProShip’s card, shown in full',
  },
  {
    fact: 'SLA owner',
    direct: 'You, with your carrier',
    proship: 'ProShip',
  },
  {
    fact: 'Day-to-day operations',
    direct: 'Yours. Missed pickups, delays and claims go to your own account manager at the carrier',
    proship: 'ProShip’s ops desk chases the courier for you, across every courier code'
,
  },
  {
    /*
      The one difference a merchant is most likely to be caught out by, so it is stated flatly.
      ProShip is not in the billing relationship on a Direct courier: the carrier weighs the parcel
      and invoices the merchant, and no charge reaches ProShip to compare, dispute or track. That is
      also why the Direct performance dashboard carries no weight-dispute metric — there is no data
      behind one, and a blank column would read as "none raised" rather than "not visible".
    */
    fact: 'Weight discrepancy',
    direct: 'Your carrier weighs and bills you directly. Disputes are yours to raise, and ProShip never sees the charge'
,
    proship: 'ProShip raises and tracks the claim with the courier, in Weight Discrepancy Management'
,
  },
  {
    fact: 'Setup needed',
    direct: 'Courier credentials. Everything else optional',
    proship: 'None — already configured',
  },
  {
    fact: 'What you pay ProShip',
    direct: 'A monthly plan or per-shipment platform fees',
    proship: 'Just shipping price, no platform fees',
  },
];
